import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, of, switchMap, throwError } from 'rxjs';
import { Endpoints } from '@/app/utils';
import { AuthService } from '@services/auth.service';
import { TokenRefreshService } from '@services/token-refresh.service';
import { ToastService } from '@services/toast.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // SecuGen device service runs on the client machine; it must not receive auth headers or cookies
  if (req.url.includes('SGIFPCapture')) {
    return next(req);
  }

  const authService = inject(AuthService);
  const tokenRefreshService = inject(TokenRefreshService);
  const toast = inject(ToastService);

  const token = authService.getAccessToken();
  const isAuthenticated = authService.isAuthenticated();

  const authReq = req.clone({
    withCredentials: true,
    ...(token ? { setHeaders: { Authorization: `Bearer ${token}` } } : {})
  });
  const isRefreshCall = req.url.includes(Endpoints.account.refreshToken);

  let hasRetried = false;

  const retryWithNewToken = (newToken: string) => {
    hasRetried = true;
    authService.setToken(newToken);
    const retryReq = req.clone({ withCredentials: true, setHeaders: { Authorization: `Bearer ${newToken}` } });
    return next(retryReq);
  };

  const forceLocalLogout = () => {
    toast.error('Something went wrong. Please login again.');
    authService.logout({ notifyServer: false });
  };

  return next(authReq).pipe(
    switchMap((event) => {
      const tokenExpired =
        !isRefreshCall &&
        !hasRetried &&
        isAuthenticated &&
        event instanceof HttpResponse &&
        Boolean((event.body as { tokenExpired?: boolean } | null)?.tokenExpired);

      if (!tokenExpired) {
        return of(event);
      }

      return tokenRefreshService.refreshAccessToken().pipe(
        switchMap((newToken) => {
          if (!newToken) {
            forceLocalLogout();
            return throwError(() => new Error('Session expired'));
          }
          return retryWithNewToken(newToken);
        })
      );
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (isRefreshCall) {
        return throwError(() => error);
      }

      if (hasRetried) {
        forceLocalLogout();
        return throwError(() => error);
      }

      return tokenRefreshService.refreshAccessToken().pipe(
        switchMap((newToken) => {
          if (!newToken) {
            forceLocalLogout();
            return throwError(() => error);
          }
          return retryWithNewToken(newToken);
        })
      );
    })
  );
};
